import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import auction from '@sorare/core/src/assets/icons/auction.svg';
import cart from '@sorare/core/src/assets/icons/cart.svg';
import transfer from '@sorare/core/src/assets/icons/transfer.svg';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { EmptyState } from '@sorare/core/src/components/EmptyState';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';

const GraphContainer = styled.div`
  --graph-padding-bottom: var(--unit);
  padding-bottom: var(--graph-padding-bottom);
  height: 200px;
  width: 100%;
  position: relative;
`;

const StyledSVG = styled.svg`
  width: 100%;
  height: 100%;
`;

// Styled components for the points and tooltips outside the SVG
const PointsContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const DataPoint = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: absolute;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

export enum PointType {
  INSTANT_BUY = 'instant-buy',
  AUCTION = 'auction',
  TRADE = 'trade',
}

const PointTypeToIcon = {
  [PointType.INSTANT_BUY]: cart,
  [PointType.AUCTION]: auction,
  [PointType.TRADE]: transfer,
};

type DataPointType = {
  x: string;
  y: number;
  tooltipContent: NonNullable<ReactNode>;
  type: PointType;
};

type GraphDataPoint = {
  x: number;
  y: number;
  tooltipContent: NonNullable<ReactNode>;
  type: PointType;
  // Add pixel positions for DOM elements
  pxX: number;
  pxY: number;
};

type Props = {
  data: DataPointType[];
  loading: boolean;
};

export const DumbGraph = ({ data, loading }: Props) => {
  const { currency, fiatCurrency } = useCurrentUserContext();
  const { formatEth, formatNumber, formatDistanceToNowStrict } =
    useIntlContext();

  // Create a ref for the container and state to keep track of its dimensions.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(200);
  const [containerHeight, setContainerHeight] = useState(200);

  useEffect(() => {
    if (!containerRef.current) return () => {};
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
      setContainerHeight(entry.contentRect.height);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const {
    leftPadding,
    margin,
    viewBoxWidth,
    viewBoxHeight,
    chartWidth,
    chartHeight,
    minDate,
    dateRange,
    minY,
    amplitude,
    lines,
    commonEthDecimals,
    points,
    pathD,
    areaPathD,
    timeTicks,
  } = useMemo(() => {
    // Define constants for layout
    /* eslint-disable @typescript-eslint/no-shadow */
    const leftPadding = 25;
    const leftBreathing = 10; // extra left space for first point
    const margin = { left: leftPadding, right: 50, top: 10, bottom: 20 };

    // Compute viewBox dimensions based on container size and left padding
    const viewBoxWidth = containerWidth + leftPadding;
    const viewBoxHeight = containerHeight;
    const chartWidth = viewBoxWidth - margin.left - margin.right;
    const chartHeight = viewBoxHeight - margin.top - margin.bottom;

    // Compute time scale based on the data timestamps
    const timestamps = data.map(({ x }) => new Date(x).getTime());
    const minDate = data.length > 0 ? Math.min(...timestamps) : 0;
    const maxDate = data.length > 0 ? Math.max(...timestamps) : 0;
    const dateRange = maxDate - minDate;

    // Compute y scale based on data values with a safety guard for zero-range.
    const dataMin = 0;
    const dataMax = Math.max(...data.map(({ y }) => y));
    const valueRange = dataMax - dataMin;
    const scale =
      valueRange === 0 ? 1 : 10 ** Math.floor(Math.log10(valueRange));
    const minY = Math.floor(dataMin / scale) * scale;
    const maxY = Math.ceil(dataMax / scale) * scale;
    const amplitude = maxY - minY;

    // Compute grid lines: steps of "scale" from 0 to amplitude
    const numGridLines = amplitude / scale + 1;
    const lines = Array.from({ length: numGridLines }).map(
      (_, index) => index * scale
    );

    // Compute common Ethereum decimals (avoid division by zero)
    const decimalsCandidates = lines.map((value, index, array) => {
      if (index === array.length - 1) return 0;
      const diff = Math.abs(value - array[index + 1]) || 1; // fallback diff = 1
      return Math.floor(-Math.log10(diff));
    });
    const filteredDecimals = decimalsCandidates.filter(num => Boolean(num));
    const commonEthDecimals =
      filteredDecimals.length > 0 ? Math.max(...filteredDecimals) : 0;

    // Transform data points to chart coordinates
    const points: GraphDataPoint[] = data.map(item => {
      const time = new Date(item.x).getTime();
      const xVal = dateRange === 0 ? 0.5 : (time - minDate) / dateRange;
      // Map the timestamp to x coordinate with extra left breathing room.
      const x =
        margin.left +
        leftBreathing +
        xVal * (viewBoxWidth - margin.right - (margin.left + leftBreathing));
      const yVal = amplitude === 0 ? 0.5 : (item.y - minY) / amplitude;
      const y = margin.top + (1 - yVal) * chartHeight; // Invert y-axis so higher values appear at the top

      // Calculate pixel positions for DOM elements
      // Convert SVG coordinates to pixel positions
      const pxX = ((x + leftPadding) * containerWidth) / viewBoxWidth;
      const pxY = (y * containerHeight) / viewBoxHeight;

      return { ...item, x, y, pxX, pxY };
    });

    // Build the SVG path for the curve using a cubic Bézier for each segment.
    let pathD = '';
    if (points.length > 0) {
      pathD = `M ${points[0].x} ${points[0].y}`;
      for (let i = 0; i < points.length - 1; i += 1) {
        const p0 = points[i];
        const p1 = points[i + 1];
        const dx = (p1.x - p0.x) / 2;
        pathD += ` C ${p0.x + dx} ${p0.y}, ${p1.x - dx} ${p1.y}, ${p1.x} ${p1.y}`;
      }
    }

    // Compute an area path that closes the shape under the curve.
    let areaPathD = '';
    if (points.length > 0) {
      areaPathD = `${pathD} L ${points[points.length - 1].x} ${
        margin.top + chartHeight
      } L ${points[0].x} ${margin.top + chartHeight} Z`;
    }

    // Compute relative time ticks for the legend.
    const numTimeTicks = 4;
    const timeTicks = Array.from({ length: numTimeTicks }).map((_, i) =>
      dateRange === 0 ? minDate : minDate + (dateRange * i) / (numTimeTicks - 1)
    );

    return {
      leftPadding,
      margin,
      viewBoxWidth,
      viewBoxHeight,
      chartWidth,
      chartHeight,
      minDate,
      dateRange,
      minY,
      amplitude,
      lines,
      commonEthDecimals,
      points,
      pathD,
      areaPathD,
      timeTicks,
    };
    /* eslint-enable @typescript-eslint/no-shadow */
  }, [data, containerWidth, containerHeight]);

  const formatGridLabel = (value: number) => {
    if (currency === Currency.FIAT) {
      return formatNumber(value, {
        style: 'currency',
        currency: fiatCurrency.code,
        maximumFractionDigits: 0,
      });
    }
    return formatEth(value, {
      minimumFractionDigits: commonEthDecimals,
      maximumFractionDigits: commonEthDecimals,
    });
  };

  if (loading) {
    return (
      <GraphContainer ref={containerRef}>
        <LoadingIndicator />
      </GraphContainer>
    );
  }

  if (data.length === 0) {
    return (
      <GraphContainer ref={containerRef}>
        <EmptyState
          title={
            <FormattedMessage
              id="Market.priceGraph.noData"
              defaultMessage="No data"
            />
          }
        />
      </GraphContainer>
    );
  }

  return (
    <GraphContainer ref={containerRef}>
      <StyledSVG
        viewBox={`-${leftPadding} 0 ${viewBoxWidth} ${viewBoxHeight}`}
        preserveAspectRatio="xMinYMin meet"
      >
        <defs>
          <linearGradient
            id="blueGradient"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1={`${margin.top}`}
            x2="0"
            y2={`${margin.top + chartHeight}`}
          >
            <stop
              offset="0%"
              stopColor="var(--c-brand-600)"
              stopOpacity="0.4"
            />
            <stop
              offset="100%"
              stopColor="var(--c-brand-600)"
              stopOpacity="0.05"
            />
          </linearGradient>
        </defs>

        {/* Render grid lines and their labels */}
        {lines.map((lineValue, index) => {
          const fraction = lineValue / amplitude;
          const y = margin.top + (1 - fraction) * chartHeight || 0;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <g key={index}>
              <line
                x1={margin.left}
                x2={margin.left + chartWidth}
                y1={y}
                y2={y}
                stroke="var(--c-nd-100)"
                strokeWidth="2"
              />
              <text
                x={margin.left - 5}
                y={y}
                textAnchor="end"
                alignmentBaseline="middle"
                fill="var(--c-nd-600)"
                fontSize="14"
              >
                {formatGridLabel(minY + lineValue)}
              </text>
            </g>
          );
        })}

        {/* Render the blue gradient area under the curve */}
        {areaPathD && (
          <path d={areaPathD} fill="url(#blueGradient)" stroke="none" />
        )}

        {/* Render the curved line connecting the data points */}
        <path
          d={pathD}
          fill="none"
          stroke="var(--c-brand-600)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Render time legend at the bottom with relative timestamps */}
        <g className="time-legend">
          {timeTicks.map((tick, i) => {
            const xPosRaw =
              margin.left +
              (dateRange === 0
                ? chartWidth / 2
                : ((tick - minDate) / dateRange) * chartWidth);
            const textMargin = 40; // safe padding from the chart edges
            const xPos = Math.max(
              margin.left + textMargin,
              Math.min(xPosRaw, margin.left + chartWidth - textMargin)
            );
            return (
              <text
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                x={xPos}
                y={margin.top + chartHeight + 15} // legend Y position offset
                textAnchor="middle"
                alignmentBaseline="middle"
                fontSize="14"
                fill="var(--c-nd-600)"
              >
                {formatDistanceToNowStrict(new Date(tick), {
                  addSuffix: true,
                })}
              </text>
            );
          })}
        </g>
      </StyledSVG>

      {/* Render points and tooltips outside the SVG */}
      <PointsContainer>
        {points.map(point => (
          <Tooltip
            key={`point-${point.type}-${point.x}-${point.y}`}
            title={point.tooltipContent}
            style={{
              position: 'absolute',
              left: `${point.pxX}px`,
              top: `${point.pxY}px`,
            }}
          >
            <DataPoint>
              <img
                src={PointTypeToIcon[point.type]}
                alt={point.type}
                width="16"
                height="16"
              />
            </DataPoint>
          </Tooltip>
        ))}
      </PointsContainer>
    </GraphContainer>
  );
};
